class GuestSessionService {
  constructor() {
    this.storageKey = 'smarttech_guest_sessions';
  }

  // Generate a unique guest session ID
  generateGuestId() {
    return 'guest_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
  }

  // Get all guest sessions
  getGuestSessions() {
    try {
      const sessions = localStorage.getItem(this.storageKey);
      return sessions ? JSON.parse(sessions) : [];
    } catch (error) {
      console.error('Error reading guest sessions:', error);
      return [];
    }
  }

  // Save a new guest session
  saveGuestSession(comparisonData, metadata = {}) {
    try {
      const sessions = this.getGuestSessions();
      const guestId = this.generateGuestId();
      
      const newSession = {
        id: guestId,
        comparisonId: comparisonData.id,
        projectName: metadata.projectName || comparisonData.project_name || 'Untitled Project',
        projectType: metadata.projectType || comparisonData.project_type || 'General',
        contractorCount: comparisonData.comparison_data?.proposals?.length || 0,
        createdAt: new Date().toISOString(),
        lastAccessed: new Date().toISOString(),
        data: comparisonData,
        metadata: {
          budget: metadata.budget,
          timeline: metadata.timeline,
          location: metadata.location,
          ...metadata
        }
      };

      sessions.push(newSession);
      localStorage.setItem(this.storageKey, JSON.stringify(sessions));
      
      return guestId;
    } catch (error) {
      console.error('Error saving guest session:', error);
      return null;
    }
  }

  // Get a specific guest session
  getGuestSession(guestId) {
    try {
      const sessions = this.getGuestSessions();
      const session = sessions.find(s => s.id === guestId);
      
      if (session) {
        // Update last accessed time
        session.lastAccessed = new Date().toISOString();
        this.updateGuestSession(guestId, session);
      }
      
      return session;
    } catch (error) {
      console.error('Error getting guest session:', error);
      return null;
    }
  }

  // Update an existing guest session
  updateGuestSession(guestId, updatedData) {
    try {
      const sessions = this.getGuestSessions();
      const sessionIndex = sessions.findIndex(s => s.id === guestId);
      
      if (sessionIndex !== -1) {
        sessions[sessionIndex] = {
          ...sessions[sessionIndex],
          ...updatedData,
          lastAccessed: new Date().toISOString()
        };
        localStorage.setItem(this.storageKey, JSON.stringify(sessions));
        return true;
      }
      
      return false;
    } catch (error) {
      console.error('Error updating guest session:', error);
      return false;
    }
  }

  // Delete a guest session
  deleteGuestSession(guestId) {
    try {
      const sessions = this.getGuestSessions();
      const filteredSessions = sessions.filter(s => s.id !== guestId);
      localStorage.setItem(this.storageKey, JSON.stringify(filteredSessions));
      return true;
    } catch (error) {
      console.error('Error deleting guest session:', error);
      return false;
    }
  }

  // Clean up old sessions (older than 30 days)
  cleanupOldSessions() {
    try {
      const sessions = this.getGuestSessions();
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
      
      const activeSessions = sessions.filter(session => {
        const createdDate = new Date(session.createdAt);
        return createdDate > thirtyDaysAgo;
      });
      
      localStorage.setItem(this.storageKey, JSON.stringify(activeSessions));
      return sessions.length - activeSessions.length; // Return number of cleaned sessions
    } catch (error) {
      console.error('Error cleaning up sessions:', error);
      return 0;
    }
  }

  // Get session statistics
  getSessionStats() {
    const sessions = this.getGuestSessions();
    return {
      total: sessions.length,
      recentSessions: sessions.filter(s => {
        const lastAccessed = new Date(s.lastAccessed);
        const sevenDaysAgo = new Date();
        sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
        return lastAccessed > sevenDaysAgo;
      }).length,
      projectTypes: [...new Set(sessions.map(s => s.projectType))],
      totalContractors: sessions.reduce((sum, s) => sum + s.contractorCount, 0)
    };
  }

  // Check if user has any guest sessions
  hasGuestSessions() {
    return this.getGuestSessions().length > 0;
  }

  // Get recent sessions (last 7 days)
  getRecentSessions() {
    const sessions = this.getGuestSessions();
    const sevenDaysAgo = new Date();
    sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
    
    return sessions
      .filter(session => new Date(session.lastAccessed) > sevenDaysAgo)
      .sort((a, b) => new Date(b.lastAccessed) - new Date(a.lastAccessed));
  }

  // Export sessions for backup
  exportSessions() {
    const sessions = this.getGuestSessions();
    const exportData = {
      exportDate: new Date().toISOString(),
      sessions: sessions,
      version: '1.0'
    };
    
    return JSON.stringify(exportData, null, 2);
  }
}

// Create singleton instance
const guestSessionService = new GuestSessionService();

export default guestSessionService; 